import { type KeyboardEvent, type ReactNode, useId, useRef } from 'react';
import { NavLink } from 'react-router';
import { cn } from '../../lib/cn';

type Tab<T extends string> = { value: T; label: ReactNode; count?: number; icon?: ReactNode };

/** Вкладки в межах сторінки (role="tablist"): стрілки ←/→, Home/End, автоматична активація. */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  className,
  size = 'md',
}: {
  tabs: Tab<T>[];
  value: T;
  onChange: (v: T) => void;
  label: string;
  className?: string;
  size?: 'sm' | 'md';
}) {
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  const onKey = (e: KeyboardEvent) => {
    const idx = tabs.findIndex((t) => t.value === value);
    let next = -1;
    if (e.key === 'ArrowRight') next = (idx + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (idx - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    e.preventDefault();
    const t = tabs[next]!;
    onChange(t.value);
    ref.current?.querySelector<HTMLElement>(`[data-value="${t.value}"]`)?.focus();
  };
  return (
    <div ref={ref} role="tablist" aria-label={label} onKeyDown={onKey} className={cn('flex items-center gap-1 overflow-x-auto', className)}>
      {tabs.map((t) => {
        const active = t.value === value;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            id={`${id}-${t.value}`}
            data-value={t.value}
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(t.value)}
            className={cn(
              'relative inline-flex shrink-0 items-center gap-1.5 rounded-sm px-2.5 font-medium whitespace-nowrap transition-colors',
              size === 'sm' ? 'h-7 text-meta' : 'h-8 text-body',
              active ? 'bg-surface-3 text-fg' : 'text-fg-2 hover:bg-surface-2 hover:text-fg',
            )}
          >
            {t.icon}
            {t.label}
            {t.count !== undefined && <span className="tnum text-caption text-fg-3">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Вкладки-маршрути (подання проєкту): звичайні посилання з aria-current. */
export function RouteTabs({ tabs, label, className }: { tabs: { to: string; label: ReactNode; icon?: ReactNode; end?: boolean }[]; label: string; className?: string }) {
  return (
    <nav aria-label={label} className={cn('flex items-center gap-1 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden', className)}>
      {tabs.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          replace
          className={({ isActive }) =>
            cn(
              'relative inline-flex h-10 shrink-0 items-center gap-1.5 px-2.5 text-body font-medium whitespace-nowrap transition-colors',
              'after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full',
              isActive ? 'text-fg after:bg-accent' : 'text-fg-2 hover:text-fg after:bg-transparent',
            )
          }
        >
          {t.icon}
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}
